import { Controller, Control } from 'react-hook-form';
import { DateObject } from 'react-multi-date-picker';
import DatePicker from '@enerex/unified-ui-react/form/DatePicker';
//import './index.css';

export type HyperDatePickerMode = 'date' | 'datetime' | 'time';

type HyperDatePickerProps = {
    name?: string;
    multiple?: boolean;
    range?: boolean;
    placeholder?: string;
    validation?: Record<string, any>;
    control?: Control<any>;
    disabled?: boolean;
    isInvalid?: boolean;
    minDate?: Date | DateObject | string | number;
    maxDate?: Date | DateObject | string | number;
    mode?: HyperDatePickerMode;
    onlyMonthPicker?: boolean;
    datePanelRight?: boolean;
    disabledDates?: Date[];
    datesClassnames?: {
        date: Date;
        className: string;
    }[];
    enableSundays?: boolean;
    closeOnSelect?: boolean;
    highlightToday?: boolean;
    readOnly?: boolean;
    clearable?: boolean;
    isClearable?: boolean;
    monthsShown?: number;
    leftPlugins?: any[];
    rightPlugins?: any[];
};

const isSameDate = (date: Date, target: Date) => {
    return (
        date.getFullYear() === target.getFullYear() &&
        date.getMonth() === target.getMonth() &&
        date.getDate() === target.getDate()
    );
};

const HyperDatePicker = ({
    name,
    control,
    multiple,
    range,
    placeholder,
    validation,
    disabled,
    isInvalid,
    minDate,
    maxDate,
    mode = 'date',
    onlyMonthPicker = false,
    disabledDates,
    datesClassnames,
    enableSundays,
    readOnly = false,
    clearable,
    isClearable,
    monthsShown,
    leftPlugins,
    rightPlugins
}: HyperDatePickerProps) => {
    const preserveTime = mode === 'datetime' || mode === 'time';
    const effectiveMonthsShown = monthsShown ?? (range ? 2 : undefined);

    const toDateInstance = (input: any) => {
        if (!input) return null;

        let nextDate: Date | null = null;

        if (input instanceof Date) {
            nextDate = new Date(input);
        } else if (
            input instanceof DateObject &&
            typeof input.toDate === 'function'
        ) {
            nextDate = input.toDate();
        } else if (typeof input === 'string' || typeof input === 'number') {
            const parsed = new Date(input);

            if (!Number.isNaN(parsed.getTime())) {
                nextDate = parsed;
            }
        }

        if (nextDate && !preserveTime) {
            nextDate.setHours(0, 0, 0, 0);
        }

        return nextDate;
    };

    const toPickerValue = (value: any) => {
        if (range) {
            const rangeValue = Array.isArray(value) ? value : [];
            const startDate = toDateInstance(rangeValue[0]);
            const endDate = toDateInstance(rangeValue[1]);

            return [startDate, endDate];
        }

        if (multiple) {
            const multipleValue = Array.isArray(value) ? value : [];
            const dates = multipleValue
                .map((item) => toDateInstance(item))
                .filter(Boolean);

            return dates.length ? dates : null;
        }

        return toDateInstance(value);
    };

    const normalizeChangeValue = (value: any) => {
        if (!value) return null;

        if (Array.isArray(value)) {
            const dates = value
                .map((item) => normalizeChangeValue(item))
                .filter(Boolean);

            return dates.length ? dates : null;
        }

        return toDateInstance(value);
    };

    const normalizedMinDate = toDateInstance(minDate);
    const normalizedMaxDate = toDateInstance(maxDate);
    const normalizedDisabledDates = disabledDates
        ?.map((date) => toDateInstance(date))
        .filter(Boolean);
    const dayClassName = datesClassnames?.length
        ? (date: Date) => {
              return datesClassnames.find((item) =>
                  isSameDate(date, item.date)
              )?.className;
          }
        : undefined;

    return (
        <Controller
            control={control}
            name={name}
            rules={validation}
            render={({ field: { onChange, value, onBlur } }) => (
                <div
                    onKeyDownCapture={(event) => {
                        if (readOnly) {
                            event.preventDefault();
                        }
                    }}
                    onBeforeInputCapture={(event) => {
                        if (readOnly) {
                            event.preventDefault();
                        }
                    }}
                >
                    <DatePicker
                        value={toPickerValue(value)}
                        onChange={(date) => {
                            onChange(normalizeChangeValue(date));
                        }}
                        onBlur={onBlur}
                        selectRange={range || undefined}
                        selectMultiple={!range && multiple ? true : undefined}
                        disabled={disabled}
                        invalid={isInvalid}
                        placeholder={placeholder}
                        minDate={normalizedMinDate}
                        maxDate={normalizedMaxDate}
                        excludeDates={normalizedDisabledDates}
                        dayClassName={dayClassName}
                        enableSundays={enableSundays}
                        isClearable={isClearable ?? clearable}
                        monthsShown={effectiveMonthsShown}
                        leftPlugins={leftPlugins}
                        rightPlugins={rightPlugins}
                        showMonthYearPicker={onlyMonthPicker}
                        showTimeSelect={mode === 'datetime'}
                        showTimeSelectOnly={mode === 'time'}
                    />
                </div>
            )}
        />
    );
};

export default HyperDatePicker;
